/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useParams } from "react-router-dom";
import { Fragment, useState, useEffect } from "react";
import { Tab } from "@headlessui/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import ArticleIcon from "../Icons/ArticleIcon";
import VideoIcon from "../Icons/VideoIcon";
import Button from "../Shared/Button";
import ArrowIcon from "../Icons/ArrowIcon";
import CoursePreviewIcon from "../Icons/CoursePreviewIcon";
import CourseContentIcon from "../Icons/CourseContentIcon";
import Spinner from "../Shared/Spinner";
import { getUserCountry } from "../../utils/utils";
import ReactMarkdown from "react-markdown";
import { GetBookBySlug } from "../../api/Books/GetBookBySlug";
import { Helmet } from "react-helmet";
import BookPreview from "./BookPreview";
import BookContent from "./BookContent";
import CertificateIcon from "../Icons/CertificateIcon";
import { getApiErrorMessage } from "../../utils/apiError";
import { queryClient } from "../../main";
import { toast } from "react-toastify";
import { AddBookToCart } from "../../api/Cart/AddBookToCart";
import { useUserDataStore } from "../../stores/user";
import { getAccessToken } from "../../lib/session";
const BookMain = () => {
  const { id } = useParams();
  const bookSlug = id || "";
  const navigate = useNavigate();
  const [country, setCountry] = useState<string | null>(null);
  const { userData } = useUserDataStore();
  useEffect(() => {
    const userCountry = getUserCountry();
    setCountry(userCountry);
  }, []);

  const { data: BookBySlugDTO, isLoading: isBookLoading } = useQuery({
    queryFn: () => GetBookBySlug(bookSlug),
    queryKey: ["books", bookSlug],
    staleTime: 0,
    refetchInterval: 0,
  });
  const book = BookBySlugDTO?.data;
  const { mutate } = useMutation({
    //@ts-expect-error
    mutationFn: () => AddBookToCart(book.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cart"] });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, "Error Adding To Cart"));
    },
  });

  if (isBookLoading)
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner />
      </div>
    );
  const handleBuyNowClick = () => {
    const accessToken = getAccessToken();
    if (accessToken) {
      mutate()
    } else {
      navigate("/login?cart=true");
    }
  };

  const discountPercentage = book?.discountPercentage || 0;
  const discountedPrice =
    country === "EG"
      ? book?.public_EGP_price
        ? book.public_EGP_price -
        (book.public_EGP_price * discountPercentage) / 100
        : 0
      : book?.public_USD_price
        ? book.public_USD_price -
        (book.public_USD_price * discountPercentage) / 100
        : 0;

  return (
    <div className="container xl:pt-20 lg:pt-28 md:pt-32 sm:pt-20 pt-28">
      <Helmet>
        <link
          rel="canonical"
          href={`https://www.nilewisect.com/book/${book?.name}`}
        />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <div className="w-full relative md:h-[600px] sm:h-[450px] h-[250px] rounded-2xl overflow-hidden">
        <img
          src={book?.img_url}
          className="w-full h-full object-fit"
        />
      </div>

      <div className="flex flex-col justify-between gap-10 pt-5 pb-32 sm:pt-8 xl:flex-row">
        <div className="w-full">
          <Tab.Group>
            <Tab.List className="bg-primary bg-opacity-[0.07] gap-1.5 flex sm:w-fit w-full sm:p-1.5 p-1 border border-primary/5 rounded-full">
              <Tab as={Fragment}>
                {({ selected }) => (
                  <button
                    className={` flex gap-1 justify-center h-8 sm:w-48 w-full rounded-full items-center outline-none transition-all duration-300 ease-out sm:text-base text-sm
                      ${selected
                        ? "text-primary bg-white border border-[#CFDBEC] "
                        : " text-primary/70"
                      }
                    `}
                  >
                    <CoursePreviewIcon /> Book Preview
                  </button>
                )}
              </Tab>
              <Tab as={Fragment}>
                {({ selected }) => (
                  <button
                    className={` flex gap-1 justify-center h-8 sm:w-48 w-full rounded-full items-center outline-none transition-all duration-300 ease-out sm:text-base text-sm
                      ${selected
                        ? "text-primary bg-white border border-[#CFDBEC] "
                        : " text-primary/70"
                      }
                    `}
                  >
                    <CourseContentIcon /> Book Content
                  </button>
                )}
              </Tab>
            </Tab.List>
            <Tab.Panels>
              <Tab.Panel>
                <BookPreview book={book} />
              </Tab.Panel>
              <Tab.Panel>
                <BookContent book={book} />
              </Tab.Panel>
            </Tab.Panels>
          </Tab.Group>
        </div>
        <div className="flex flex-col gap-6 xl:space-y-7 xl:block lg:flex-row lg:gap-10">
          <div className="sm:py-9 xl:w-[429px] w-full sm:px-6 p-[18px] border border-black/15 shadow rounded-2xl">
            <span className="text-lightGray font-medium sm:text-0.5xl  mb-2.5 sm:mb-3">
              Full Book
            </span>
            <div className="flex items-center justify-between mb-2 sm:mb-7">
              <h1 className="text-primary font-bold text-lg sm:text-[30px] -tracking-[0.02em] flex items-center gap-4">
                {discountedPrice} {country === "EG" ? "EGP" : "USD"}{" "}
                {discountPercentage > 0 && (
                  <span className="text-lightGray font-medium sm:text-0.5xl line-through">
                    {country === "EG" ? (
                      <>{book?.public_EGP_price} EGP</>
                    ) : (
                      <>{book?.public_USD_price} USD</>
                    )}
                  </span>
                )}
              </h1>
              {discountPercentage > 0 && (
                <span className="mr-2 bg-[#FF0000]/5 py-2.5 leading-none px-2.5 rounded-lg text-[#FF0000]/70 font-semibold text-lg">
                  {discountPercentage}%
                </span>
              )}
            </div>
            <h1 className="text-burntBlack font-bold sm:text-0.5xl tracking-[0.02em] mb-3 sm:mb-5">
              Book Includes:
            </h1>
            <div className="font-medium sm:text-xl text-sm text-lightGray sm:space-y-2.5 space-y-2 mb-4 sm:mb-8">
              <div className="flex gap-2.5 items-center">
                <ArticleIcon className="sm:size-auto size-5" />{" "}
                {book?.bookChapters.length} Chapters
              </div>
              <div className="flex gap-2.5 items-center">
                <CertificateIcon className="sm:size-auto size-5" /> Full access to all book chapters {" "}
              </div>
            </div>
            <Button
              variant="primary"
              className="w-full sm:!h-16 !h-11 sm:!text-xl !text-base mb-1 sm:mb-3"
              onClick={handleBuyNowClick}
              disabled={userData.role === "sales"}
            >
              Add To Cart
            </Button>
            <p className="tracking-[0.02em] sm:text-base text-sm font-medium text-center">
              Trusted Learning Experience
            </p>
          </div>
          <div className="sm:pt-10 xl:w-[429px] w-full  sm:pb-12 sm:px-8  p-[18px] border border-black/15 shadow rounded-2xl">
            <div className="flex justify-between mb-5 sm:mb-7">
              <h1 className="text-burntBlack font-bold sm:text-0.5xl tracking-[0.02em]">
                Publisher
              </h1>
              <Link
                to={`/instructor/${book?.author?.id}`}
                className="flex gap-0.5 items-center text-lightGray sm:text-lg text-sm font-medium"
              >
                View Profile{" "}
                <ArrowIcon className="rotate-180 sm:size-5 size-3" />{" "}
              </Link>
            </div>
            <div className="flex items-center sm:gap-4 gap-2.5 mb-4">
              <div className="sm:size-[60px] size-12 rounded-full overflow-hidden border border-black/20">
                <img
                  className="object-cover object-[25%_75%] "
                  src={book?.author?.photo}
                  alt="Instructor"
                />
              </div>
              <div className="font-bold text-burntBlack sm:text-xl">
                <h1 className="mb-0.5 sm:text-lg -tracking-[0.02em]">
                  Dr. {book?.author?.firstName}{" "}
                  {book?.author?.lastName}
                </h1>
                <p className="font-medium text-lightGray ">Instructor</p>
              </div>
            </div>
            <p className="mb-5 text-sm font-medium text-lightGray sm:text-base">
              <ReactMarkdown>{book?.author?.description}</ReactMarkdown>
            </p>
            <div className="flex gap-10 2xl:justify-between">
              <div className="flex gap-2.5 items-center font-medium text-sm sm:text-xl text-lightGray">
                <VideoIcon className="sm:size-auto size-5" />{" "}
                {book?.author?.count_students} Students
              </div>
              <div className="flex gap-2.5 items-center font-medium text-sm sm:text-xl text-lightGray">
                <ArticleIcon className="sm:size-auto size-5" />{" "}
                {book?.author?.count_courses} Courses
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookMain;
